// src/components/TransactionHistory.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import './TransactionHistory.css';

function TransactionHistory() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const transactionsQuery = query(
      collection(db, 'revenue_transactions'),
      orderBy('timestamp', 'desc'),
      limit(showAll ? 1000 : 50)
    );
    
    const unsubscribe = onSnapshot(transactionsQuery, (snapshot) => {
      const transactionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTransactions(transactionsData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching transactions:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [showAll]);

  // حساب إحصائيات سريعة
  const stats = {
    totalTransactions: transactions.length,
    totalAdded: transactions
      .filter(t => t.operation_type === 'إضافة')
      .reduce((sum, t) => sum + (t.amount || 0), 0),
    totalDeducted: transactions
      .filter(t => t.operation_type === 'خصم')
      .reduce((sum, t) => sum + (t.amount || 0), 0),
    shopifyCount: transactions.filter(t => t.platform_type === 'shopify').length,
    easyorderCount: transactions.filter(t => t.platform_type === 'easyorder').length,
  };

  // 🔥 تصدير سجل العمليات إلى Excel
  const exportTransactionsToExcel = () => {
    try {
      // تحضير البيانات للتصدير
      const exportData = transactions.map(transaction => ({
        'التاريخ': transaction.date,
        'الوقت': new Date(transaction.timestamp).toLocaleTimeString('ar-EG'),
        'اسم البراند': transaction.brand_name,
        'رقم العميل': transaction.client_id,
        'المنصة': transaction.platform_type === 'shopify' ? 'Shopify' : 
                   transaction.platform_type === 'easyorder' ? 'EasyOrder' : 'غير محدد',
        'نوع العملية': transaction.operation_type,
        'المبلغ': `${transaction.amount}$`,
        'الرصيد قبل': `${transaction.balance_before}$`,
        'الرصيد بعد': `${transaction.balance_after}$`,
        'فرق الرصيد': `${transaction.balance_after - transaction.balance_before}$`
      }));

      // إنشاء ورقة عمل
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // تعديل عرض الأعمدة
      const colWidths = [
        { wch: 20 }, // التاريخ
        { wch: 12 }, // الوقت
        { wch: 20 }, // اسم البراند
        { wch: 12 }, // رقم العميل
        { wch: 12 }, // المنصة
        { wch: 12 }, // نوع العملية
        { wch: 12 }, // المبلغ
        { wch: 12 }, // الرصيد قبل
        { wch: 12 }, // الرصيد بعد
        { wch: 12 }  // فرق الرصيد
      ];
      ws['!cols'] = colWidths;

      // إنشاء مصنف
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'سجل العمليات');

      // إضافة ورقة إحصائيات
      const statsData = [
        { 'البند': 'إجمالي العمليات', 'القيمة': stats.totalTransactions },
        { 'البند': 'إجمالي المضاف ($)', 'القيمة': stats.totalAdded.toFixed(2) },
        { 'البند': 'إجمالي المخصوم ($)', 'القيمة': stats.totalDeducted.toFixed(2) },
        { 'البند': 'الصافي ($)', 'القيمة': (stats.totalAdded - stats.totalDeducted).toFixed(2) },
        { 'البند': 'عمليات Shopify', 'القيمة': stats.shopifyCount },
        { 'البند': 'عمليات EasyOrder', 'القيمة': stats.easyorderCount }
      ];
      const wsStats = XLSX.utils.json_to_sheet(statsData);
      wsStats['!cols'] = [{ wch: 25 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, wsStats, 'الإحصائيات');

      // تحميل الملف
      const fileName = `سجل_العمليات_${new Date().toLocaleDateString('ar-EG').replace(/\//g, '-')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      console.log('✅ تم تصدير سجل العمليات بنجاح');
    } catch (error) {
      console.error('❌ خطأ في تصدير سجل العمليات:', error);
      alert('حدث خطأ أثناء تصدير البيانات');
    }
  };

  if (loading) {
    return <div className="transaction-loading">جاري تحميل سجل العمليات...</div>;
  }

  return (
    <div className="transaction-history-container">
      <div className="transaction-header">
        <h2>📜 سجل عمليات الرصيد</h2>
        <div style={{display: 'flex', gap: '10px'}}>
          <button 
            onClick={exportTransactionsToExcel}
            className="export-btn"
            title="تصدير إلى Excel"
          >
            📥 تصدير Excel
          </button>
          <button 
            onClick={() => setShowAll(!showAll)} 
            className="toggle-view-btn"
          >
            {showAll ? '📋 عرض آخر 50' : '📊 عرض الكل'}
          </button>
        </div>
      </div>

      {/* إحصائيات سريعة */}
      <div className="transaction-stats">
        <div className="stat-item add">
          <span className="stat-label">إجمالي المضاف</span>
          <span className="stat-value">+{stats.totalAdded.toFixed(2)} ج</span>
        </div>
        <div className="stat-item deduct">
          <span className="stat-label">إجمالي المخصوم</span>
          <span className="stat-value">-{stats.totalDeducted.toFixed(2)} ج</span>
        </div>
        <div className="stat-item shopify">
          <span className="stat-label">🛒 Shopify</span>
          <span className="stat-value">{stats.shopifyCount}</span>
        </div>
        <div className="stat-item easyorder">
          <span className="stat-label">📦 EasyOrder</span>
          <span className="stat-value">{stats.easyorderCount}</span>
        </div>
        <div className="stat-item total">
          <span className="stat-label">إجمالي العمليات</span>
          <span className="stat-value">{stats.totalTransactions}</span>
        </div>
      </div>

      {/* جدول العمليات */}
      <div className="transactions-table-wrapper">
        <table className="transactions-table">
          <thead>
            <tr>
              <th>التاريخ والوقت</th>
              <th>اسم البراند</th>
              <th>المنصة</th>
              <th>نوع العملية</th>
              <th>المبلغ</th>
              <th>الرصيد قبل</th>
              <th>الرصيد بعد</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length > 0 ? (
              transactions.map((transaction) => (
                <tr key={transaction.id} className={`transaction-row ${transaction.operation_type === 'إضافة' ? 'add-row' : 'deduct-row'}`}>
                  <td className="date-cell">
                    <div className="date-main">{transaction.date}</div>
                    <div className="date-sub">{new Date(transaction.timestamp).toLocaleTimeString('ar-EG')}</div>
                  </td>
                  <td className="brand-cell">
                    <strong>{transaction.brand_name}</strong>
                    <div className="client-id">ID: {transaction.client_id}</div>
                  </td>
                  <td className="platform-cell">
                    <span className={`platform-badge ${transaction.platform_type}`}>
                      {transaction.platform_type === 'shopify' ? '🛒 Shopify' : 
                       transaction.platform_type === 'easyorder' ? '📦 EasyOrder' : 
                       '❓ غير محدد'}
                    </span>
                  </td>
                  <td className="operation-cell">
                    <span className={`operation-badge ${transaction.operation_type === 'إضافة' ? 'add' : 'deduct'}`}>
                      {transaction.operation_type === 'إضافة' ? '➕ إضافة' : '➖ خصم'}
                    </span>
                  </td>
                  <td className={`amount-cell ${transaction.operation_type === 'إضافة' ? 'add' : 'deduct'}`}>
                    <strong>{transaction.operation_type === 'إضافة' ? '+' : '-'}{transaction.amount} $</strong>
                  </td>
                  <td className="balance-cell">{transaction.balance_before} $</td>
                  <td className="balance-cell final">{transaction.balance_after} $</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={{textAlign: 'center', padding: '30px', color: '#6272a4'}}>
                  لا توجد عمليات مسجلة حتى الآن
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="transaction-footer">
        <p className="footer-note">
          💡 يتم تسجيل جميع عمليات إضافة وخصم الرصيد تلقائياً
        </p>
      </div>
    </div>
  );
}

export default TransactionHistory;
